import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from '@studio-freight/lenis';
import { Toaster } from "./components/ui/toaster";
import { SEO } from "./components/SEO";

// Components
import { Navigation } from "./components/Navigation";
import { HeroSection } from "./components/HeroSection";
import { AboutSection } from "./components/AboutSection";
import { ServicesSection } from "./components/ServicesSection";
import { MidPageCta } from "./components/MidPageCta";
import { ValuePropsSection } from "./components/ValuePropsSection";
import { TechStackSection } from "./components/TechStackSection";
import { TestimonialsSection } from "./components/TestimonialsSection";
import { ContactSection } from "./components/ContactSection";
import { Footer } from "./components/Footer";

// Pages - code-split so homepage doesn't ship their JS upfront
const ConsultationPage = lazy(() =>
  import("./pages/ConsultationPage").then((m) => ({ default: m.ConsultationPage }))
);
const InstantQuotePage = lazy(() =>
  import("./pages/InstantQuotePage").then((m) => ({ default: m.InstantQuotePage }))
);
const MaintenancePage = lazy(() =>
  import("./pages/MaintenancePage").then((m) => ({ default: m.MaintenancePage }))
);
// Chat widget pulls in socket.io-client - split out of the critical path
const ChatWidget = lazy(() => import("./components/ChatWidget"));

const HomePage = () => {
  return (
    <div data-testid="home-page">
      <SEO
        title="LOLCorp PVT LTD | Custom Software & Website Development | UK Enterprise Solutions"
        description="LOLCorp builds custom software and websites for businesses. Custom software from $5,000, websites from $1,000. UK-based, fixed-scope, 4-6 week delivery. Get an instant quote today."
        path="/"
      />
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <MidPageCta />
        <TestimonialsSection />
        <ValuePropsSection />
        <TechStackSection />
        <ContactSection />
      </main>
      <Footer />
      <Suspense fallback={null}>
        <ChatWidget serverUrl="https://sales.loljs.com" />
      </Suspense>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Handle hash navigation for smooth scroll
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          setTimeout(() => {
            lenis.scrollTo(element, { offset: -80 });
          }, 100);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    // Initial check for hash
    if (window.location.hash) {
      handleHashChange();
    }

    return () => {
      lenis.destroy();
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <div className="App">
      {/* Subtle grain texture overlay */}
      <div className="grain-overlay" />
      
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route path="/instant-quote" element={<InstantQuotePage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      
      <Toaster />
    </div>
  );
}

export default App;
